<template>
  <div>
    <Section>
      <SectionHeader :title="$t('apps.inventory.message.general')"/>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.inventory.label.warehousename')} *`" />
        <BaseInput
          v-model="$v.input.name.$model"
          :classNames="[{ 'is-invalid': $v.input.name.$error }]"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label>
          {{ $t('apps.inventory.label.warehousenameshort') }} *
          <vs-tooltip
            :text="$t('apps.inventory.message.runningcodenumber')"
            position="top"
            style="float: right;margin-left: 5px;">
            <i class="fas fa-info-circle"/>
          </vs-tooltip>
        </Label>
        <BaseInput
          v-model="$v.input.nameShort.$model"
          :classNames="['text-uppercase', { 'is-invalid': $v.input.nameShort.$error }]"
          :invalidText="
            input.nameShort && $v.input.nameShort.isExist === false
              ? $t('apps.inventory.message.warehouseshortnameisalreadyexist')
              : null
          "
          :disabled="disabled"
          :maxLength="6"
        />
        </Col>
        <Col
          v-if="input.nameShort"
          className="col-lg-6">
        <Label :text="$t('apps.inventory.label.preview')"/>
        <div class="preview-short-name">
          <span
            v-html="$t('apps.inventory.message.previewwarehousecode', {
              name: nameShortUppercase,
              code: `${moment().format('YYYY')}0001`,
            })"
          />
        </div>
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.inventory.message.location')"/>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.inventory.label.address')" />
        <BaseTextArea
          v-model="input.address"
          :disabled="disabled"
          :rows="3"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.inventory.message.binlocation')"/>
      <FormRow>
        <Col className="col-lg-12">
        <BaseButton
          :disabled="disabled"
          className="btn-light"
          @click="onClickOpenModal"
        >
          {{ $t('apps.inventory.button.addbinlocation') }} ({{ input.binLocations.length }})
        </BaseButton>
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.inventory.message.assignskus')"/>
      <FormRow>
        <Col className="col-lg-12">
        <BaseNotFoundData
          v-if="!products.length"
          :title="$t('apps.inventory.message.notfoundskus')"
          :detail="$t('apps.inventory.message.createnewproducts')"
        />
        <BaseAssignSkus
          v-else
          v-model="input.assignSkus"
          :items="assignItems"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.inventory.message.setting')"/>
      <FormRow>
        <Col className="col-lg-6">
        <BaseCheckbox
          v-model="input.primary"
          :label="$t('apps.inventory.message.useprimarywarehouse')"
          :disabled="disabled || disabledCheckBoxPrimary"
        />
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      v-if="showActionButtons"
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onCancel="handleCancel"
      @onSubmit="handleSubmit"
    />

    <BaseBinLocationModal
      :show="modal.show"
      :loading="modal.loading"
      :binLocations="binLocations"
      @onClose="handleCloseModal"
      @onCancel="handleCancleModal"
      @onSubmit="handleSumitModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'

export default {
  props: {
    state: Object,
    isSubmitted: Boolean,
    disabled: Boolean,
    warehouses: {
      type: Array,
      default: () => ([]),
    },
    products: {
      type: Array,
      default: () => ([]),
    },
    binLocations: {
      type: Array,
      default: () => ([]),
    },
    showActionButtons: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      input: {
        name: null,
        nameShort: null,
        address: null,
        primary: false,
        assignSkus: [],
        binLocations: [],
      },
      inputOriginal: null,
      modal: {
        show: false,
        loading: false,
      },
    }
  },

  validations: {
    input: {
      name: {
        required,
      },
      nameShort: {
        required,
        isExist: function (value) {
          if (!value) return false
          if (this.isCreatePage) {
            return !this.warehouses.some((wh) => {
              return wh.short_name.toUpperCase() === value.toUpperCase()
            })
          }
          return !this.warehouses.some((wh) => {
            return wh.short_name.toUpperCase() === value.toUpperCase() && this.state.warehouse_id !== wh.warehouse_id
          })
        },
      },
    },
  },

  computed: {
    ...mapGetters({
      getSkuInfoBySkuId: 'getSkuInfoBySkuId',
      getProductInfoBySkuId: 'getProductInfoBySkuId',
      getUsedLotSerialSkus: 'getUsedLotSerialSkus',
      isSkuAssignInWh: 'isSkuAssignInWh',
    }),
    usedLotSkuIds () {
      return [...this.getUsedLotSerialSkus.usedLotSkuIds]
    },
    usedSerialSkuIds () {
      return [...this.getUsedLotSerialSkus.usedSerialSkuIds]
    },
    isEditPage () {
      return !!this.state
    },
    isCreatePage () {
      return !this.state
    },
    nameShortUppercase () {
      return this.input.nameShort
        ? this.input.nameShort.trim().toUpperCase()
        : null
    },
    assignItems () {
      const whId = this.state ? this.state.warehouse_id : null
      return this.products.reduce((acc, cur) => {
        const skus = cur.prices.map((price) => {
          const isUsedLot = this.usedLotSkuIds.some((skuId) => price.sku_id === skuId)
          const isUsedSerial = this.usedSerialSkuIds.some((skuId) => price.sku_id === skuId)
          const isSkuAssignInWh = this.isSkuAssignInWh(price.sku_id, whId)
          return {
            text: this.getProductFullName(cur.product_name, price.attributes),
            value: price.sku_id,
            useLot: false,
            useSerialNumber: false,
            disabled: isSkuAssignInWh === true && (isUsedLot || isUsedSerial),
          }
        })
        return acc.push(...skus) && acc
      }, [])
    },
    disabledCheckBoxPrimary () {
      return this.isEditPage && this.state.primary
    },
  },

  watch: {
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    if (this.state) {
      this.setInput(this.state)
    } else {
      this.inputOriginal = cloneDeep(this.input)
    }
  },

  methods: {
    handleCancel () {
      if (this.$listeners.onCancel) {
        this.$emit('onCancel')
      } else {
        this.input = cloneDeep(this.inputOriginal)
        this.scrollToTop(600)
      }
    },
    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToTop(600)
      } else {
        this.$emit('onSubmit', this.input)
      }
    },
    setInput (state) {
      this.input.name = state.warehouse_name
      this.input.nameShort = state.short_name
      this.input.address = state.info.address
      this.input.primary = state.primary
      this.input.assignSkus = state.assign_skus
        .filter((item) => {
          const productInfo = this.getProductInfoBySkuId(item.sku_id)
          return productInfo != null
        })
        .map((item) => {
          const skuInfo = this.getSkuInfoBySkuId(item.sku_id)
          const productInfo = this.getProductInfoBySkuId(item.sku_id)
          return {
            text: this.getProductFullName(productInfo.product_name, skuInfo.attributes),
            value: item.sku_id,
            useLot: item.is_use_lot,
            useSerialNumber: item.is_use_serial_number,
            active: true,
          }
        })
      this.input.binLocations = this.binLocations.map((bin) => {
        return {
          id: bin._id,
          code: bin.code,
        }
      })
      this.inputOriginal = cloneDeep(this.input)
    },
    onClickOpenModal () {
      this.modal.show = true
    },
    handleCloseModal () {
      this.modal.show = false
    },
    handleCancleModal () {
      this.modal.show = false
    },
    handleSumitModal (binLocations = []) {
      this.modal.show = false
      this.input.binLocations = binLocations
    },
  },
}
</script>

<style scoped>
.preview-short-name {
  display: flex;
  align-items: center;
  width: 100%;
  height: 35px;
  border: solid 1px #ced4da;
  border-radius: 4px;
  color: #495057;
  background: #e9ecef;
  padding: .375rem .75rem;
}
</style>



// WEBPACK FOOTER //
// src/components/combine/WmsWarehouseForm.vue